import { useState, forwardRef, useEffect, useMemo } from "react"
import { CaretDownIcon, CaretUpIcon } from "@modulz/radix-icons"
import { useNavigation } from "./Root"
import { Flex } from "../Flex"
import { CSSFn } from "../styles"
import { StyledGroupWithLink } from "./styles/LinkStyle"

type GroupWithLinkOwnProps = Readonly<{
  name: string
  value: string
  css?: CSSFn
  defaultOpen?: boolean
  closeMenu?: () => void
  setNavTriggered: (triggered: boolean) => void
}>

type GroupWithLinkIconProps = Readonly<{
  width: string
  height: string
  onClick: (event: React.MouseEvent) => void
}>

export type GroupWithLinkProps = React.ComponentPropsWithRef<typeof Flex> & GroupWithLinkOwnProps

export const GroupWithLink: React.FC<GroupWithLinkProps> = forwardRef(
  (
    { children, name, value, defaultOpen = false, closeMenu, setNavTriggered, ...containerProps }: GroupWithLinkProps,
    forwardedRef
  ) => {
    const iconProps: GroupWithLinkIconProps = useMemo(
      () => ({
        width: `25px`,
        height: `25px`,
        onClick: (event: React.MouseEvent) => {
          event.stopPropagation()
          setIsOpen((currentState) => !currentState)
        },
      }),
      []
    )
    const [isOpen, setIsOpen] = useState<boolean>(defaultOpen)
    const [iconComponent, setIconComponent] = useState<JSX.Element>(<CaretUpIcon {...iconProps} />)
    const { state, dispatch } = useNavigation(`Link`)

    const isHome = value === `/`
    const valueUrlMapping = isHome ? value : `/${value}`
    const isActive = isHome ? state.value === valueUrlMapping : state.value.endsWith(valueUrlMapping)

    useEffect(() => {
      setIconComponent(() => (isOpen ? <CaretUpIcon {...iconProps} /> : <CaretDownIcon {...iconProps} />))
    }, [isOpen, iconProps])

    return (
      <Flex flexDirection="column" ref={forwardedRef} {...containerProps}>
        <StyledGroupWithLink
          variant="heading"
          state={isActive ? `active` : `inactive`}
          data-active={isActive ? `active` : `inactive`}
          onClick={() => {
            setNavTriggered(true)
            if (closeMenu) closeMenu()
            dispatch({ type: `navigate`, value: valueUrlMapping })
          }}
        >
          {name}
          {iconComponent}
        </StyledGroupWithLink>

        {isOpen && <Flex flexDirection="column">{children}</Flex>}
      </Flex>
    )
  }
)
